import { useStyles } from '@archipro-design/aria';

import type { PaginationOptions } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { Pagination, Navigation, Keyboard } from 'swiper/modules';
import classnames from 'classnames';
import { ArrowLeftSLine, ArrowRightSLine } from '@archipro-design/icons';

import { useState, useRef } from 'react';

import * as S from '@modules/professional/component/about-tab/testimonial/Testimonials.style';
import type { SlideProps } from '@modules/professional/component/about-tab/testimonial/TestimonialSlidePage';
import { TestimonialSlidePage } from '@modules/professional/component/about-tab/testimonial/TestimonialSlidePage';
import type { ProfessionalDetailsTestimonial } from '@modules/professional/component/about-tab/testimonial/Testimonials.mobile';
import { TestimonialPaginationSection } from './TestimonialPagination.style';
import { useTracker } from '@archipro-website/tracker';

import { PAGE_ANCHORS } from '@modules/professional/config/constants';
import { useScrollToHandle } from '@modules/root/hook/use-scrollto-handle';

import PageSection from '@modules/professional/component/page-layout/PageSection';

SwiperCore.use([Navigation, Pagination, Keyboard]);

export const TestimonialsDesktop: React.FC<{
    professionalTestimonials: ProfessionalDetailsTestimonial[];
}> = ({ professionalTestimonials }) => {
    const pagesPerSlide = 3;
    const paginationBounds = 4;
    const upperLimitBound = paginationBounds + 1;
    const basicPaginationLimit = 8;

    const [inLowerBound, setInLowerBound] = useState(true);
    const [inUpperBound, setInUpperBound] = useState(false);
    const [index, setIndex] = useState(0);
    const { css } = useStyles({ index, inUpperBound, inLowerBound });
    const tracker = useTracker();

    const anchorRef = useRef<HTMLDivElement>(null);
    useScrollToHandle(PAGE_ANCHORS.TESTIMONIALS, anchorRef.current);

    const pageTotal = Math.ceil(
        professionalTestimonials.length / pagesPerSlide
    );

    const pagination: PaginationOptions = {
        clickable: true,
        el: '.swiper-pagination',
        type: 'custom',
        renderCustom: (_, current: number, total: number) => {
            let htmlSpan = '';
            const createSpan = (index: number) => {
                if (total < basicPaginationLimit) {
                    const classText = JSON.stringify(
                        css(S.PaginationSpan) +
                            ' ' +
                            'swiper-pagination-bullet' +
                            (index !== current - 1
                                ? ''
                                : ' swiper-pagination-bullet-active') +
                            ' isSimple'
                    );

                    return `<span class=${classText}>${index + 1}</span>`;
                }
                const isLast = index === total - 1;
                const isFirst = index === 0;
                const isLower = index <= paginationBounds;
                const isUpper = index >= pageTotal - upperLimitBound;
                const classText = JSON.stringify(
                    css(S.PaginationSpan) +
                        ' ' +
                        'swiper-pagination-bullet' +
                        (index !== current - 1
                            ? ''
                            : ' swiper-pagination-bullet-active') +
                        (!isFirst && isLower ? ' lowerBound' : '') +
                        (isUpper ? ' upperBound' : '') +
                        (!isLower && !isUpper ? ' regular' : '') +
                        (isFirst || isLast ? ' topBottomPage' : '')
                );

                if (isFirst)
                    return `<span><span class=${classText}>${
                        index + 1
                    }</span><span class="ellipsisFirst">&#8943</span></span>`;
                if (isLast)
                    return `<span class="ellipsisLast">&#8943</span><span class=${classText}>${
                        index + 1
                    }</span>`;
                return `<span class=${classText}>${index + 1}</span>`;
            };

            for (let index = 0; index < total; index++) {
                htmlSpan += createSpan(index);
            }

            return htmlSpan;
        },
    };

    const updatePagination = (num: number) => {
        const pageIndex = num + 1;
        setInLowerBound(pageIndex <= paginationBounds);
        setInUpperBound(pageIndex > pageTotal - paginationBounds);
        setIndex(pageIndex);
    };

    const testimonialPages = [
        ...Array(professionalTestimonials.length).keys(),
    ].reduce((testimonialPage: SlideProps[][], a) => {
        const key = Math.floor(a / pagesPerSlide);
        const testimonial = professionalTestimonials[a];
        if (!testimonial) {
            return testimonialPage;
        }
        const page = testimonialPage[key];
        if (page) {
            page.push(testimonial);
        } else {
            testimonialPage.push([testimonial]);
        }
        return testimonialPage;
    }, []);

    return (
        <div className={css(S.TestimonialsWrapper)} ref={anchorRef}>
            <PageSection sectionTitle={'Testimonials'}>
                <Swiper
                    navigation={{
                        nextEl: '.slider-button-next',
                        prevEl: '.slider-button-prev',
                    }}
                    pagination={pagination}
                    allowSlideNext
                    allowSlidePrev
                    centeredSlides={true}
                    centeredSlidesBounds={true}
                    keyboard={{
                        enabled: true,
                    }}
                    allowTouchMove
                    className={css(S.TestimonialSlider)}
                    onSlideNextTransitionEnd={(swiperCore) => {
                        const { realIndex } = swiperCore;

                        tracker.log('TestimonialScroll', {
                            url: new URL(window.location.href),
                            targetTracker: 'archiproTracker',
                            data: {
                                ExtraData: realIndex + 1, // starts from 1
                            },
                        });

                        updatePagination(realIndex);
                    }}
                >
                    {testimonialPages.map((page, index) => (
                        <SwiperSlide
                            key={index}
                            className={css(S.TestimonialSlide)}
                        >
                            <TestimonialSlidePage SlideArray={page} />
                        </SwiperSlide>
                    ))}

                    {professionalTestimonials.length > pagesPerSlide && (
                        <div className={css(S.TestimonialSliderControls)}>
                            <div className={css(S.ButtonBox)}>
                                <button
                                    className={classnames(
                                        css(S.ButtonSlider),
                                        'slider-button-prev'
                                    )}
                                >
                                    <ArrowLeftSLine
                                        className={css(S.TestimonialSliderPrev)}
                                    />
                                </button>
                                <button
                                    className={classnames(
                                        css(S.ButtonSlider),
                                        'slider-button-next'
                                    )}
                                >
                                    <ArrowRightSLine
                                        size={'1x'}
                                        className={css(S.TestimonialSliderPrev)}
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                    {professionalTestimonials.length > pagesPerSlide && (
                        <div className={css(TestimonialPaginationSection)}>
                            <div className={'swiper-pagination'}></div>
                        </div>
                    )}
                </Swiper>
            </PageSection>
        </div>
    );
};
